import { create } from "zustand";
import { createAvatar } from "@dicebear/core";
import { pixelArt, avataaars, personas, lorelei } from "@dicebear/collection";
import { userAuthAPI } from "../api/userAuthAPI";
import { leaderboardAPI } from "../api/leaderboardAPI";
import { SESSION_ID } from "../api/apiConfig";
import { useEventTrackingStore } from "./eventTrackingStore";
import { defaultSettings } from "../data/avatars";
import { cleanup } from "../utils/audioManager";

const styles = {
	pixelArt,
	avataaars,
	personas,
	lorelei,
};

// Initialize storyMuted from localStorage (default true)
const initialStoryMuted = (() => {
	try {
		const stored = localStorage.getItem("storyMuted");
		if (stored === null) {
			return false;
		} // default to unmuted
		return stored === "true";
	} catch (e) {
		return false;
	}
})();

const useUserStore = create((set, get) => ({
	user: {
		AvatarURL: "",
		ClientID: 0,
		Email: "",
		FirstName: "",
		Joined: "",
		LastName: "",
		LastScore: "",
		MemberID: 0,
		Notes: "",
		Phone: "",
		RecruitedBy: 0,
		SellerID: 0,
		Start: "",
		Status: "0",
		Type: "0",
		Username: "",
		Zipcode: "",
		PrevContest: 0,
		CreatureID: 0,
		CurrentLumens: 0,
		LifetimeLumens: 0,
	},
	user_roles: [],
	currentContest: null,
	contestStatuses: {}, // Store contest statuses by contestId
	selectedContestID: null,
	character: {
		style: "avataaars",
		settings: defaultSettings.avataaars,
	},
	avatar: null,
	userProgress: {
		isAuthenticated: false,
		onboardingStep: 0, // 0=not started, 1=story started, 2=creature selected, 3=quest started, 4=first quest completed
		hasCompletedFirstQuest: false,
		accessLevel: 0, // 0=not signed in, 1=basic access, 2=full access
	},
	loginAttemptId: null,
	isGamemaster: false,
	isRingleader: false,
	isJudge: false,
	storyMuted: initialStoryMuted,
	beamkins: [],
	currentBeamkin: {},
	setUser: (user) => {
		set((state) => ({
			...state,
			user,
		}));
	},
	setUserRoles: (user_roles) => {
		set((state) => {
			const roles = user_roles || [];
			return {
				...state,
				user_roles: roles,
				isGamemaster: roles.some((role) => role.Role === "gamemaster"),
				isRingleader: roles.some((role) => role.Role === "ringleader"),
				isJudge: roles.some((role) => role.Role === "judge"),
			};
		});
	},
	setBeamkins: (beamkins) => {
		set((state) => ({
			...state,
			beamkins: beamkins || [],
		}));
	},
	setCurrentBeamkin: (id, name, imageURL) => {
		set((state) => ({
			...state,
			currentBeamkin: { id, name, imageURL },
		}));
	},
	appendBeamkin: (beamkin) => {
		set((state) => ({
			...state,
			beamkins: [...state.beamkins, beamkin],
		}));
	},
	setUserWithRoles: (user, user_roles) => {
		console.log("setUserWithRoles", user, user_roles);
		set((state) => {
			const roles = user_roles || [];
			return {
				user: { ...state.user, ...user },
				user_roles: roles,
				isGamemaster: roles.some((role) => role.Role === "gamemaster"),
				isRingleader: roles.some((role) => role.Role === "ringleader"),
				isJudge: roles.some((role) => role.Role === "judge"),
			};
		});
	},
	updateUser: (userData) => {
		set((state) => ({
			...state,
			user: { ...state.user, ...userData },
		}));
	},
	setStoryMuted: (storyMuted) => {
		try {
			localStorage.setItem("storyMuted", String(storyMuted));
		} catch (e) {
			console.error("Error setting storyMuted:", e);
		}
		set((state) => ({
			...state,
			storyMuted,
		}));
	},
	// Convenience getters for the new userProgress system
	getIsAuthenticated: () => {
		return get().userProgress.isAuthenticated;
	},
	getOnboardingStep: () => {
		return get().userProgress.onboardingStep;
	},
	getHasCompletedFirstQuest: () => {
		return get().userProgress.hasCompletedFirstQuest;
	},
	getAccessLevel: () => {
		return get().userProgress.accessLevel;
	},
	// Legacy compatibility getter
	getUserAccountCompletionLevel: () => {
		const progress = get().userProgress;
		// Map new system back to old levels for compatibility
		switch (progress.onboardingStep) {
			case 1:
				return 1;
			case 2:
				return 2;
			case 3:
				return 3;
			case 4:
				return 4;
			default:
				return 0;
		}
	},
	// Reusable method to set up user progress after account retrieval
	setupUserProgressFromAccount: (accountResponse) => {
		const state = get();
		const userProgress = {
			isAuthenticated: true,
			onboardingStep: 0,
			hasCompletedFirstQuest: false,
			accessLevel: 2, // Default to full access for authenticated users
			tutorialProgress: accountResponse.result.tutorial_progress,
		};

		if (accountResponse.result.beamkins) {
			// Always set the beamkins array
			state.setBeamkins(accountResponse.result.beamkins);

			if (accountResponse.result.beamkins.length === 0) {
				console.log("User onboarding step: 1");
				if (
					localStorage.getItem("creature") === 4 ||
					localStorage.getItem("creature") === 5
				) {
					userProgress.onboardingStep = 2; //Needs to finish registering account
				} else {
					userProgress.onboardingStep = 1; //Needs to start story
				}
			} else {
				const hasBeamkin4 = accountResponse.result.beamkins.some(
					(beamkin) => beamkin.Creature === 4
				);
				const hasBeamkin5 = accountResponse.result.beamkins.some(
					(beamkin) => beamkin.Creature === 5
				);
				if (hasBeamkin4 && hasBeamkin5) {
					userProgress.onboardingStep = 4; //Finished first quest
					userProgress.hasCompletedFirstQuest = true;
					userProgress.accessLevel = 2; //Full access
				} else {
					userProgress.onboardingStep = 3; //Needs to finish first quest
				}
				if (accountResponse.result.beamkin) {
					state.setCurrentBeamkin(
						accountResponse.result.beamkin.ID,
						accountResponse.result.beamkin.Name,
						accountResponse.result.beamkin.ImageURL
					);
				}
			}
		} else if (
			localStorage.getItem("creature") === "4" ||
			localStorage.getItem("creature") === "5"
		) {
			console.log("Found account registration started", 2);
			userProgress.onboardingStep = 2; //Needs to finish registering account
		} else {
			userProgress.onboardingStep = 1; //Needs to start story
		}

		// Set the unified user progress
		state.setUserProgress(userProgress);
		console.log("User progress:", userProgress);

		// Load storyMuted from account response if available (fallback if not loaded from session)
		if (accountResponse.result.member?.StoryMuted !== undefined) {
			state.setStoryMuted(accountResponse.result.member.StoryMuted);
			console.log(
				"Loaded storyMuted from account (StoryMuted):",
				accountResponse.result.member.StoryMuted
			);
		} else if (accountResponse.result.member?.AudioPreference !== undefined) {
			// AudioPreference: "with" = unmuted (false), "without" = muted (true)
			const shouldBeMuted =
				accountResponse.result.member.AudioPreference === "without";
			state.setStoryMuted(shouldBeMuted);
			console.log(
				"Loaded storyMuted from account (AudioPreference):",
				shouldBeMuted
			);
		}

		return userProgress;
	},
	getAvatarStyle: () => {
		return styles[get().character.style];
	},
	setCurrentContest: (contest) => {
		set((state) => {
			// Only update if the contest is actually different
			const currentId = state.currentContest?.ContestID;
			const newId = contest?.ContestID;

			if (currentId === newId && currentId !== null && newId !== null) {
				console.log(
					"setCurrentContest: Skipping update - same contest ID:",
					newId
				);
				return state; // Return same state if contest ID hasn't changed
			}

			console.log(
				"setCurrentContest: Updating contest from",
				currentId,
				"to",
				newId
			);

			return {
				...state,
				currentContest: contest,
			};
		});
	},
	setContestStatus: (contestId, status) => {
		set((state) => ({
			contestStatuses: {
				...state.contestStatuses,
				[contestId]: status,
			},
		}));
	},
	getContestStatus: (contestId) => {
		const state = get();
		// Simply return the status without any side effects
		return state.contestStatuses[contestId] || "Active";
	},
	setCharacter: (character) => {
		console.log(
			"🔴 setCharacter called with skin color:",
			character.settings?.skinColor
		);
		console.trace("🔴 setCharacter call stack");

		set((state) => {
			console.log(
				"🔴 Previous character skin color:",
				state.character.settings?.skinColor
			);
			return {
				...state,
				character,
			};
		});
		console.log(
			"🔴 After set, getting current character:",
			get().character.settings?.skinColor
		);
	},
	setCharacterSetting: (setting, value) => {
		// TRAP: Show stack trace when setting skinColor to the OLD value
		if (setting === "skinColor" && value[0] === "b59776") {
			console.log("🚨🚨🚨 TRAP: Setting skinColor to OLD value b59776!");
			console.trace("🚨 setCharacterSetting stack trace");
		}

		set((state) => {
			const newSettings = { ...state.character.settings, [setting]: value };
			return {
				...state,
				character: {
					...state.character,
					settings: newSettings,
				},
			};
		});
	},
	buildCharacter: (data) => {
		console.log(
			"🟢 buildCharacter called - OVERWRITING character with server skin color:",
			data.SkinColor
		);
		console.trace("buildCharacter call stack");
		// Structures character settings from API response and builds avatar from settings
		const newCharacter = {
			style: data.Style,
			settings: {
				size: 128,
				seed: [data.Seed],
				body: [data.Body],
				backgroundColor: [data.BackgroundColor],
				beard: [data.Beard],
				beardProbability: [100],
				clothing: [data.Clothing],
				clothingColor: [data.ClothingColor ? data.ClothingColor : "1e81b0"],
				clothesColor: [
					data.ClothesColor ? data.ClothesColor : data.ClothingColor,
				], // avataaars uses clothesColor
				eyes: [data.Eyes],
				glasses: [data.Glasses],
				hair: [data.Hair],
				top: [data.Top ? data.Top : data.Hair], // avataaars uses 'top' for hair
				hairColor: [data.HairColor],
				hatColor: [data.HatColor],
				mouth: [data.Mouth],
				facialHair: [data.FacialHair],
				facialHairColor: [data.FacialHairColor],
				facialHairProbability: [100],
				accessories: [data.Accessories],
				accessoriesColor: [data.AccessoriesColor],
				accessoriesProbability: [100],
				mood: [data.Mood],
				eyebrows: [data.Eyebrows],
				freckles: [data.Freckles],
				frecklesProbability: [100],
				head: [data.Head],
				nose: [data.Nose],
				skinColor: [data.SkinColor ? data.SkinColor : "8D5524"],
			},
		};
		newCharacter.settings.facialHairProbability = [100];
		newCharacter.settings.beardProbability = [100];
		newCharacter.settings.frecklesProbability = [100];
		newCharacter.settings.glassesProbability = [100];
		newCharacter.settings.moodProbability = [100];
		const avatar = createAvatar(
			styles[newCharacter.style],
			newCharacter.settings
		).toDataUri();
		set((state) => ({
			...state,
			character: newCharacter,
			avatar,
			// Only update user.AvatarURL if it's not already set (preserve server URL)
			// The server URL should take precedence over the generated data URI
			user: {
				...state.user,
				AvatarURL: state.user.AvatarURL || avatar,
				MediaURL: state.user.MediaURL || avatar,
			},
			// Update role flags based on existing user_roles
			isGamemaster: state.user_roles.some((role) => role.Role === "gamemaster"),
			isRingleader: state.user_roles.some((role) => role.Role === "ringleader"),
			isJudge: state.user_roles.some((role) => role.Role === "judge"),
		}));
	},
	buildAvatar: (character) => {
		// Builds avatar from structred character settings
		const avatar = createAvatar(
			styles[character.style],
			character.settings
		).toDataUri();
		set((state) => ({
			...state,
			avatar,
			// Only update user.AvatarURL if it's not already set (preserve server URL)
			// The server URL should take precedence over the generated data URI
			user: {
				...state.user,
				AvatarURL: state.user.AvatarURL || avatar,
				MediaURL: state.user.MediaURL || avatar,
			},
		}));
	},
	setAvatar: (avatarUrl) => {
		// Sets avatar directly (for uploaded photos)
		// Also update user.AvatarURL to keep them in sync
		set((state) => ({
			...state,
			avatar: avatarUrl,
			user: {
				...state.user,
				AvatarURL: avatarUrl,
				MediaURL: avatarUrl,
			},
		}));
	},
	setUserProgress: (progressData) => {
		set((state) => ({
			...state,
			userProgress: { ...state.userProgress, ...progressData },
		}));
	},
	// Legacy compatibility methods - these will update the new userProgress system
	setAccessLevel: (accessLevel) => {
		set((state) => ({
			...state,
			userProgress: {
				...state.userProgress,
				accessLevel,
				isAuthenticated: accessLevel > 0,
			},
		}));
	},
	setUserAccountCompletionLevel: (level) => {
		set((state) => {
			let onboardingStep = 0;
			let hasCompletedFirstQuest = false;
			let { accessLevel } = state.userProgress;

			// Map old levels to new system
			switch (level) {
				case 1:
					onboardingStep = 1; // story started
					break;
				case 2:
					onboardingStep = 2; // creature selected
					break;
				case 3:
					onboardingStep = 3; // quest started
					break;
				case 4:
					onboardingStep = 4; // first quest completed
					hasCompletedFirstQuest = true;
					accessLevel = 2; // full access
					break;
				default:
					onboardingStep = 0;
			}

			return {
				...state,
				userProgress: {
					...state.userProgress,
					onboardingStep,
					hasCompletedFirstQuest,
					accessLevel,
					isAuthenticated: accessLevel > 0,
				},
			};
		});
	},
	setStyle: (style) => {
		set((state) => ({
			...state,
			character: {
				...state.character,
				style,
			},
		}));
	},
	handleSignOut: async (navigate) => {
		const sessionid = SESSION_ID();
		try {
			const response = await userAuthAPI.signout(sessionid);

			// Clean up both BGM players
			cleanup();

			// Clear event tracking on logout
			useEventTrackingStore.getState().clear();

			// Clear all localStorage
			localStorage.clear();

			// Clear all sessionStorage (includes character editor state and cached settings)
			try {
				sessionStorage.clear();
			} catch (error) {
				console.error("Error clearing sessionStorage:", error);
			}

			set((state) => ({
				...state,
				user: {},
				user_roles: [],
				currentContest: null,
				contestStatuses: {},
				character: {
					style: "avataaars",
					settings: defaultSettings.avataaars,
				},
				avatar: null,
				userProgress: {
					isAuthenticated: false,
					onboardingStep: 0,
					hasCompletedFirstQuest: false,
					accessLevel: 0,
				},
				loginAttemptId: null,
				isGamemaster: false,
				isRingleader: false,
				isJudge: false,
				storyMuted: true,
				beamkins: [],
				currentBeamkin: {},
			}));

			navigate("/");
		} catch (error) {
			console.error("Signout error:", error);
		}
	},
	setLoginAttemptId: (loginAttemptId) => {
		set((state) => ({
			...state,
			loginAttemptId,
		}));
	},
	hasRole: (roleType, contestId = null) => {
		const state = get();
		if (!state.user_roles || state.user_roles.length === 0) {
			return false;
		}

		if (contestId) {
			return state.user_roles.some(
				(role) => role.Role === roleType && role.ContestID === contestId
			);
		}

		return state.user_roles.some((role) => role.Role === roleType);
	},
	getRolesByType: (roleType) => {
		const state = get();
		if (!state.user_roles || state.user_roles.length === 0) {
			return [];
		}

		return state.user_roles.filter((role) => role.Role === roleType);
	},
	getRolesByContest: (contestId) => {
		const state = get();
		if (!state.user_roles || state.user_roles.length === 0) {
			return [];
		}

		return state.user_roles.filter(
			(role) => String(role.ContestID) === String(contestId)
		);
	},
	getUserRoleByContestId: (contestId) => {
		const state = get();
		if (!state.user_roles || state.user_roles.length === 0) {
			return null;
		}

		return (
			state.user_roles.find(
				(role) => String(role.ContestID) === String(contestId)
			) || null
		);
	},
	getRolesByContestName: (contestName) => {
		const state = get();
		if (!state.user_roles || state.user_roles.length === 0) {
			return [];
		}

		return state.user_roles.filter((role) => role.ContestName === contestName);
	},
	hasRoleForContest: (roleType, contestId) => {
		const state = get();
		if (!state.user_roles || state.user_roles.length === 0) {
			return false;
		}
		return state.user_roles.some(
			(role) =>
				role.Role === roleType && String(role.ContestID) === String(contestId)
		);
	},
	getContestNames: () => {
		const state = get();
		if (!state.user_roles || state.user_roles.length === 0) {
			return [];
		}

		return [...new Set(state.user_roles.map((role) => role.ContestName))];
	},
	updateRoleFlags: () => {
		const state = get();
		const roles = state.user_roles || [];
		set((state) => ({
			...state,
			isGamemaster: roles.some((role) => role.Role === "gamemaster"),
			isRingleader: roles.some((role) => role.Role === "ringleader"),
			isJudge: roles.some((role) => role.Role === "judge"),
		}));
	},
	getIsJudgeOnly: () => {
		const state = get();
		if (state.isJudge && !state.isGamemaster) {
			return true;
		}
		return false;
	},
	getGameMasterComponentRoles: () => {
		const state = get();
		if (state.getIsJudgeOnly()) {
			return state.user_roles;
		}
		if (!state.user_roles || state.user_roles.length === 0) {
			return [];
		}

		// Filter out member roles and null/undefined contest IDs
		const nonMemberRoles = state.user_roles.filter(
			(role) =>
				(role.Role === "judge" || role.Role === "gamemaster") &&
				role.ContestID !== null &&
				role.ContestID !== undefined
		);

		// Group roles by ContestID
		const rolesByContest = {};
		nonMemberRoles.forEach((role) => {
			const contestId = role.ContestID;
			if (!rolesByContest[contestId]) {
				rolesByContest[contestId] = [];
			}
			rolesByContest[contestId].push(role);
		});

		// For each contest, prioritize GameMaster role if it exists
		const prioritizedRoles = Object.values(rolesByContest).map((roles) => {
			// Check if there's a GameMaster role in this contest
			const gameMasterRole = roles.find((role) => role.Role === "gamemaster");
			if (gameMasterRole) {
				return gameMasterRole;
			}
			// If no GameMaster, return the first role (could be judge, ringleader, etc.)
			return roles[0];
		});

		// Return unique contest IDs from the prioritized roles
		return prioritizedRoles;
	},

	// Get contests that the user has participated in using the leaderboardAPI
	getUserContests: async () => {
		const state = get();
		const MemberID = state.user?.MemberID;

		if (!MemberID) {
			console.warn("getUserContests: No MemberID found in user state");
			return [];
		}

		try {
			console.log("getUserContests: Fetching contests for MemberID:", MemberID);
			const response = await leaderboardAPI.getContestsByMember({
				memberid: MemberID,
			});

			// Extract contests from the response
			const contests =
				response?.data?.result?.contests ||
				response?.data?.result ||
				response?.data ||
				[];

			console.log("getUserContests: Raw API response:", response);
			console.log("getUserContests: Extracted contests:", contests);

			// If contests is an array of contest objects, return the full objects
			if (Array.isArray(contests)) {
				// Return the full contest objects with contestID, contestName, zipcode
				return contests;
			}

			return [];
		} catch (error) {
			console.error("getUserContests: Error fetching contests:", error);
			return [];
		}
	},
}));

export default useUserStore;
